import {Alert, Button, Checkbox, Link, RadioGroup, Select, Textarea, TextField, IconButton,  Icon} from '@bonprix/design-system';
import {useEffect, useMemo, useRef, useState, useTransition} from 'react';
import fetchWithCsrf from '../../../../../../../core/src/main/resources/scripts/helper/fetch-with-csrf.js';
import {WidgetInstance} from 'friendly-challenge';
import ModelContainer from '@core/scripts/components/model-container';
import {insertReactComponentIntoString} from '../helpers.jsx';
import Url from '@core/scripts/helper/url';

const model = ModelContainer.get('servicePageModel');

export function FeedbackAppForm({cdaEntry}) {
    const [, startTransition] = useTransition();
    const [formValues, setFormValues] = useState({
        firstName: model.user.firstName ?? '',
        lastName: model.user.lastName ?? '',
        customerId: model.user.customerId ?? '',
        email: model.user.email ?? '',
        emailCompare: model.user.email ?? '',
        message: '',
        starRating: 0,
        'frc-captcha-solution': '',
    });
    const [submitButtonIsDisabled, setSubmitButtonIsDisabled] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [formErrors, setFormErrors] = useState({});

    const fcContainer = useRef();
    const fcWidget = useRef();
    const friendlyCaptchaSitekey =
        model.tenantCountry === 'at'
            ? model.friendlyCaptchaSitekeyNewsletterAt
            : model.friendlyCaptchaSitekeyNewsletter;

    useEffect(() => {
        if (!model.friendlyCaptchaEnabled) {
            setSubmitButtonIsDisabled(false);
            return;
        }

        if (!fcWidget.current && fcContainer.current) {
            fcWidget.current = new WidgetInstance(fcContainer.current, {
                doneCallback: (solution) => {
                    setFormValues((prev) => ({...prev, 'frc-captcha-solution': solution}));
                    setSubmitButtonIsDisabled(false);
                },
            });
        }

        return () => {
            if (fcWidget.current != undefined) fcWidget.current.reset();
        };
    }, [fcContainer]);

    const handleChange = (name) => (value) => {
        startTransition(() => setFormValues((prev) => ({...prev, [name]: value})));
    };

    const handleStarClick = (value) => () => {
        startTransition(() => setFormValues((prev) => ({...prev, starRating: value})));
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        setFormErrors({});
        setErrorMsg('');
        setIsSubmitting(true);

        fetchWithCsrf(Url.toAbsolute(cdaEntry.fields.subscribeApiUrl), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formValues),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.errors) {
                    setFormErrors(data.errors);
                }
                if (data.errorMsg) {
                    setErrorMsg(cdaEntry.fields[data.errorMsg]);
                }

                if (data.errors || data.errorMsg) {
                    fcWidget.current.reset();
                }

                //redirect to success page
                if (data.success) {
                    window.location = Url.toAbsolute(cdaEntry.fields.successPageUrl);
                }
            })
            .catch((error) => {
                console.error(error);
                setErrorMsg(cdaEntry.fields.genericErrorMessage);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const dataProtectionLabel = (
        <span>
            {insertReactComponentIntoString(
                cdaEntry.fields.labelTermsAndConditionsAgreement,
                '#dataProtectionLink#',
                <Link href="/service/datenschutz/">Datenschutzerklärung</Link>,
            )}
        </span>
    );

    return (
        <div className="service-page-feedback-app-form">
            <h2>{cdaEntry.fields.headline}</h2>
            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelFirstName}
                        maxLength="50"
                        required
                        name="firstName"
                        value={formValues.firstName}
                        onChange={handleChange('firstName')}
                        error={formErrors && !!formErrors.firstName}
                        errorText={formErrors && formErrors.firstName && cdaEntry.fields[formErrors.firstName]}
                    />
                </div>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelLastName}
                        maxLength="50"
                        required
                        name="lastName"
                        value={formValues.lastName}
                        onChange={handleChange('lastName')}
                        error={formErrors && !!formErrors.lastName}
                        errorText={formErrors && formErrors.lastName && cdaEntry.fields[formErrors.lastName]}
                    />
                </div>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelCustomerId}
                        maxLength="50"
                        name="customerId"
                        value={formValues.customerId}
                        placeholder="optional"
                        type="number"
                        disabled={!!model.user.customerId}
                        onChange={handleChange('customerId')}
                        error={formErrors && !!formErrors.customerId}
                        errorText={formErrors && formErrors.customerId && cdaEntry.fields[formErrors.customerId]}
                    />
                </div>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelEmail}
                        maxLength="100"
                        required
                        inputMode="email"
                        type="email"
                        value={formValues.email}
                        name="email"
                        onChange={handleChange('email')}
                        error={formErrors && !!formErrors.email}
                        errorText={formErrors && formErrors.email && cdaEntry.fields[formErrors.email]}
                    />
                </div>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelEmailCompare}
                        maxLength="100"
                        required
                        inputMode="email"
                        type="email"
                        value={formValues.emailCompare}
                        name="emailCompare"
                        onChange={handleChange('emailCompare')}
                        error={formErrors && !!formErrors.emailCompare}
                        errorText={formErrors && formErrors.emailCompare && cdaEntry.fields[formErrors.emailCompare]}
                    />
                </div>
                <div className="form-row">
                    <Textarea
                        required
                        name="message"
                        label={cdaEntry.fields.labelMessage}
                        value={formValues.message}
                        onChange={handleChange('message')}
                        error={formErrors && !!formErrors.message}
                        errorText={formErrors && formErrors.message && cdaEntry.fields[formErrors.message]}
                        maxLength="2500"
                    />
                </div>

                <div className="form-row">
                    <div className="ml-2.5 mb-1.25 text-base">{cdaEntry.fields.labelStarRating}</div>
                    <div className="flex gap-1.5">
                        <Button contentWidth label="1 Star" variant="tertiary" onClick={handleStarClick(1)}>
                            <Icon variant={formValues.starRating > 0 ? "review-full" : "review"} className="w-12 h-12 group-hover:group-enabled:fill-clr-on-bg-low active:fill-clr-on-bg-low" />
                        </Button>
                        <Button contentWidth label="2 Star" variant="tertiary" onClick={handleStarClick(2)}>
                            <Icon variant={formValues.starRating > 1 ? "review-full" : "review"} className="w-12 h-12 group-hover:group-enabled:fill-clr-on-bg-low active:fill-clr-on-bg-low" />
                        </Button>
                        <Button contentWidth label="3 Star" variant="tertiary" onClick={handleStarClick(3)}>
                            <Icon variant={formValues.starRating > 2 ? "review-full" : "review"} className="w-12 h-12 group-hover:group-enabled:fill-clr-on-bg-low active:fill-clr-on-bg-low" />
                        </Button>
                        <Button contentWidth label="4 Star" variant="tertiary" onClick={handleStarClick(4)}>
                            <Icon variant={formValues.starRating > 3 ? "review-full" : "review"} className="w-12 h-12 group-hover:group-enabled:fill-clr-on-bg-low active:fill-clr-on-bg-low" />
                        </Button>
                        <Button contentWidth label="5 Star" variant="tertiary" onClick={handleStarClick(5)}>
                            <Icon variant={formValues.starRating > 4 ? "review-full" : "review"} className="w-12 h-12 group-hover:group-enabled:fill-clr-on-bg-low active:fill-clr-on-bg-low" />
                        </Button>
                    </div>
                    {formErrors && formErrors.starRating && cdaEntry.fields[formErrors.starRating] && (
                        <Alert isOpen severity="error" showCloseIcon={false}>
                            {cdaEntry.fields[formErrors.starRating]}
                        </Alert>
                    )}
                </div>

                <div className="form-row">
                    <Checkbox
                        label={dataProtectionLabel}
                        required
                        name="termsAndConditionsAgreement"
                        checked={formValues.termsAndConditionsAgreement}
                        onChange={handleChange('termsAndConditionsAgreement')}
                        value="1"
                        error={formErrors && !!formErrors.termsAndConditionsAgreement}
                        errorText={
                            formErrors &&
                            formErrors.termsAndConditionsAgreement &&
                            cdaEntry.fields[formErrors.termsAndConditionsAgreement]
                        }
                    />
                </div>

                {model.friendlyCaptchaEnabled && (
                    <div className="form-row">
                        <div
                            ref={fcContainer}
                            className="frc-captcha"
                            data-sitekey={friendlyCaptchaSitekey}
                            data-lang="de"
                        ></div>
                        {formErrors && formErrors['frc-captcha-solution'] && (
                            <Alert isOpen severity="error" showCloseIcon={false}>
                                {cdaEntry.fields[formErrors['frc-captcha-solution']]}
                            </Alert>
                        )}
                    </div>
                )}

                {errorMsg && (
                    <div className="form-row">
                        <Alert isOpen severity="error" showCloseIcon={false}>
                            {errorMsg}
                        </Alert>
                    </div>
                )}

                <div className="form-row">
                    <Button type="submit" disabled={submitButtonIsDisabled} loading={isSubmitting}>
                        {cdaEntry.fields.labelButtonSubmit}
                    </Button>
                </div>
            </form>
        </div>
    );
}
